import React, { useEffect, useMemo, useState } from "react";
import ComponentAttributeToolbar from "../AttributeTable/ComponentAttributeToolbar";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { useFetchedValues, useAttributeFormBuilder, useChangeBooleans, useComponentProperties, useFormBuilderAttributes, useAttributes } from "../../../Hooks/Display/useComponentProperties";
import { getAllAttributes } from "../../Helpers/Schema/Schema";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { filterAttributesForComponent } from "../AttributeTable/utils";
import SyncAttributeDialog from "../AttributeTable/SyncAttributeDialog/SyncAttributeDialog";
import { bySystemAttributeDesc } from "../utils";
import Skeleton from "../../Shared/Skeleton/Skeleton";
import { useSelectedPage } from "../../../Hooks/Display/usePages";
import Toolbar from "./Toolbar";
import Scrollable from "../../Shared/Edit/Scrollable";
import { getAttributesFromLayout, isFormActionsChanged, isStepsChanged } from "./utils";
import { getEntityPages, getReferenceAttributeEntityPages } from "../../../API/Display/Display";
import Steps from "./Steppers/Steps";
import StepLayout from "./Steppers/StepLayout";
import FormActions from "./Steppers/FormActions";
import CreateSection from "./Sections/CreateSection";
import { uuidv4 } from "../../../Hooks/Display/utils";
import { getAttributes } from "../../../API/Attribute/Attribute";
import EditColumnPanel from "./Columns/EditColumnPanel";

const AttributeFormBuilder = () => {

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const [properties, setProperty] = useComponentProperties();
    const [formBuilderState, { setFormBuilderAttributes, toggleFormBuilderSyncAttributeDialog, updateLayoutFromRecommendation }] = useFormBuilderAttributes();
    const [{ steps, selectedStepId, formActions, formBuilderLoading }, dispatchLayout] = useAttributeFormBuilder();
    const [fetchedValues, { updateAttributeTableFetchedValue }] = useFetchedValues();
    const entityId = usePropsStoreValue("entityId");
    const [draggedAttributeId, setDraggedAttributeId] = useState(null);
    const [{ isAttributeFormBuilderChanged }, setChangeBoolean] = useChangeBooleans();
    const [attributeTableState] = useAttributes();
    const usedFromGroupComponent = ["Cards"].includes(selectedComponent.componentType);
    const eligibleComponentTypes = ["Form", "View"];
    const eligibleSubPageTypes = ["New Record", "Edit Record", "Single Record"];
    const shouldShowOptimizeAIButton =
        eligibleComponentTypes.includes(selectedComponent.componentType) &&
        eligibleSubPageTypes.includes(selectedPage.subPageType) &&
        !usedFromGroupComponent;

    const toolbarAttributes = useMemo(() => {
        if (fetchedValues && fetchedValues.attributeTable && fetchedValues.attributeTable.attributes) {
            const attributes = filterAttributesForComponent(
                fetchedValues.attributeTable.attributes,
                selectedComponent.componentType,
                selectedPage.subPageType
            );
            const compponentAttributes = []
            for (const step of steps) {
                const attributes = getAttributesFromLayout(
                    step.layout,
                    fetchedValues.attributeTable.attributes,
                    entityId
                );
                compponentAttributes.push(...attributes);
            }
            const selectableAttributes = attributes.filter(attribute => !compponentAttributes.some(attr => attr.attributeId === attribute._id));
            const sortedSelectableAttributes = selectableAttributes.sort((a, b) => a.order - b.order).sort(bySystemAttributeDesc);
            const normalAttributes = sortedSelectableAttributes.filter(attribute => attribute.isSystemAttribute === false || attribute.isSystemAttribute === undefined);
            const systemAttributes = sortedSelectableAttributes.filter(attribute => attribute.isSystemAttribute);
            const sortAlphabetically = normalAttributes.sort((a, b) => a.attributeLabel.localeCompare(b.attributeLabel));
            return [...sortAlphabetically, ...systemAttributes];;
        }
        return [];
    }, [fetchedValues, steps])

    useEffect(() => {
        getEntityAttributes();
    }, [selectedComponent]);

    useEffect(() => {
        const isAnyStepChanged = isStepsChanged(steps, selectedComponent.steps);
        const isAnyActionsChanged = isFormActionsChanged(formActions, selectedComponent.formActions);
        const isChanged = isAnyStepChanged || isAnyActionsChanged;
        setChangeBoolean("isAttributeFormBuilderChanged", isChanged);
    }, [steps, formActions])

    async function getEntityAttributes() {
        if (formBuilderState.loading) {
            const attributes = await getAttributes(entityId);
            updateAttributeTableFetchedValue("attributes", attributes);
            const referenceAttributePages = await getReferenceAttributePages(attributes);
            updateAttributeTableFetchedValue("referenceAttributePages", referenceAttributePages);
            const attributesTableData = attributes.map(attribute => ({
                ...attribute,
                attributeId: attribute._id,
                entityId: entityId
            }));
            setFormBuilderAttributes(attributesTableData);
            getAttributeFromBuilderLayout(attributesTableData);
        }
    }

    function getAttributeFromBuilderLayout(attributesTableData) {
        if (!formBuilderLoading) return;

        // Check if the form builder is blank (no steps or empty steps)
        const isFormBuilderBlank = !steps || steps.length === 0 ||
            (steps.length === 1 &&
                (!steps[0].layout ||
                    steps[0].layout.length === 0 ||
                    (steps[0].layout.length === 1 &&
                        steps[0].layout[0].columns.length === 0)));

        if (isFormBuilderBlank) {

            // Use existing steps and form actions
            const existingSteps = selectedComponent.steps || [];
            const existingFormActions = selectedComponent.formActions || [];

            if (existingSteps.length > 0) {
                if (usedFromGroupComponent && existingSteps[0].layout[0].columns.length === 0) {
                    populateAttributesToStep(existingSteps[0], attributesTableData);
                }
                dispatchLayout.setSteps(existingSteps);
                dispatchLayout.setSelectedStep(existingSteps[0].id);
            } else {
                // Create empty step if no steps exist
                const emptyStep = {
                    id: uuidv4(),
                    name: "Step 1",
                    layout: [
                        {
                            id: uuidv4(),
                            section: "",
                            columns: []
                        }
                    ],
                    languages: {}
                };
                if (attributeTableState?.attributes?.length > 0 || selectedComponent?.attributes?.length > 0) {
                    populateAttributesToStep(emptyStep, attributesTableData);
                }
                dispatchLayout.setSteps([emptyStep]);
                dispatchLayout.setSelectedStep(emptyStep.id);
            }

            if (existingFormActions && existingFormActions.length > 0) {
                dispatchLayout.setFormActions(existingFormActions);
            } else {
                // Create default form action if none exist
                dispatchLayout.setFormActions([]);
            }
        } else {
            dispatchLayout.setSteps(selectedComponent.steps);
            dispatchLayout.setSelectedStep(selectedComponent.steps[0].id);
            dispatchLayout.setFormActions(selectedComponent.formActions);
        }
    }

    function populateAttributesToStep(step, attributesTableData) {
        try {
            const isNewOrEditForm = selectedPage.subPageType === "New Record" || selectedPage.subPageType === "Edit Record";
            const attributes = attributeTableState?.attributes?.length > 0 ? attributeTableState.attributes.slice(0, 4) : selectedComponent?.attributes?.slice(0, 4);
            for (let i = 0; i < attributes.length; i++) {
                const attribute = attributes[i];
                const originalAttribute = attributesTableData.find(attr => attr._id === attributes[i].attributeId);
                let segmentColor = [];
                if (originalAttribute && originalAttribute.attributeType === "Picklist String" && originalAttribute.picklist) {
                    const picklistValues = originalAttribute.picklist.split('ɖ').filter(value => value.trim() !== '');
                    segmentColor = picklistValues.map(() => ({ textColor: "#ffffff", fillColor: "#000000" }));
                }
                step.layout[0].columns.push({
                    i: uuidv4(),
                    attributeId: attribute.attributeId,
                    attributeType: attribute.attributeType,
                    referencedEntityId: attribute.referencedEntityId,
                    cellRules: false,
                    x: 0,
                    y: i,
                    w: 12,
                    h: attribute.attributeType === "Multiline Text" ? 2 : 1,
                    minW: 1,
                    maxW: 12,
                    minH: attribute.attributeType === "Multiline Text" ? 2 : 1,
                    columnSettings: {
                        displayType: attribute.attributeType === "Picklist String" && !isNewOrEditForm ? "SegmentedControl" : "Inherit",
                        fontSize: 14,
                        fontWeight: "regular",
                        placeholder: "",
                        segmentColor: segmentColor,
                        cellRules: false,
                        showLabel: selectedComponent.componentType === "Cards" ? true : false,
                        hyperlink: attribute.hyperlink || false,
                        targetPageId: attribute.targetPageId || ""
                    }
                });
            }
        } catch (error) {
            console.error(error);
        }
    }

    async function getReferenceAttributePages(attributes) {
        const allPages = [];
        for (const attribute of attributes) {
            attribute.entityId = entityId;
            if (attribute.attributeType === "Entity Lookup") {
                const { data: pages } = await getEntityPages(attribute.entityId, selectedPage.portalId);
                const singleAndEditRecordPages = pages.filter(page => page.subPageType === "Single Record" || page.subPageType === "Edit Record");
                allPages.push(...singleAndEditRecordPages);
            }
        }
        return allPages;
    }

    function handleRecommendation(sections) {
        const { attributes } = fetchedValues.attributeTable;
        for (const section of sections) {
            section.columns = section.columns.filter(column => (
                attributes.some(attribute => attribute._id === column.attributeId)
            ))
            for (const column of section.columns) {
                const attribute = attributes.find(attribute => attribute._id === column.attributeId);
                column.i = uuidv4();
                column.attributeType = attribute.attributeType;
                column.cellRules = attribute.showCellFormatingIfStatement && selectedComponent.componentType === "View";
                column.maxW = 12;
                switch (attribute.attributeType) {
                    case "Multiline Text":
                        column.minH = 2;
                        column.minW = 6;
                        column.h = 2;
                        break;
                    case "Attachment":
                        column.minH = 2;
                        column.minW = 6;
                        column.h = 2;
                        break;
                    case "Html":
                        column.minH = 3;
                        column.minW = 6;
                        column.h = 3;
                        break;
                    default:
                        column.minH = 1;
                        column.minW = 1;
                        column.h = 1;
                        break;
                }
            }
        }
        dispatchLayout.updateLayoutFromRecommendation(sections);
    }

    if (formBuilderState.loading) {
        return (
            <Skeleton
                rows={[
                    { columns: [{ size: 12 }] },
                    { full: true }
                ]} />
        )
    }

    return (
        <div className="container-fluid h-100">
            {attributeTableState.showSyncAttributeDialog && <SyncAttributeDialog componentTypes={["Table", "Cards"]} />}
            <ComponentAttributeToolbar
                onRecommend={handleRecommendation}
                showSelectAttributeButton={false}
                showOptimizeAIButton={shouldShowOptimizeAIButton}
                isOptimizeButtonDisabled={steps.length > 1}
            />
            <div className="row form-builder-container">
                <div className="col-2 form-builder-toolbar">
                    <Scrollable>
                        <Toolbar attributes={toolbarAttributes} onDragAttribute={setDraggedAttributeId} />
                    </Scrollable>
                </div>
                <div className="col-10 form-builder d-flex flex-column">
                    {properties.isMultiStepForm ?
                        <Steps draggedAttributeId={draggedAttributeId} onDropAttribute={setDraggedAttributeId} />
                        :
                        <div className="form-builder-step-layout">
                            {selectedComponent.componentType !== "Cards" && <CreateSection />}
                            <StepLayout draggedAttributeId={draggedAttributeId} onDropAttribute={setDraggedAttributeId} />
                        </div>
                    }
                    <FormActions step={steps.findIndex(step => step.id === selectedStepId)} />
                </div>
                <EditColumnPanel />
            </div>
        </div>
    );
};

export default AttributeFormBuilder;
